import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Link as LinkIcon } from 'lucide-react';
import type { ListBlock } from '@/types/contentBlocks';
import type { CitationSource } from '@/utils/citationReplacer';

interface ListBlockProps {
  block: ListBlock;
  isDarkMode: boolean;
  citationSources?: CitationSource[];
}

/* ----------------------- Utilities ----------------------- */

const getHostname = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const getHighQualityFavicon = (url: string) => {
  try {
    const { hostname } = new URL(url);
    return `https://www.google.com/s2/favicons?domain=${hostname}&sz=128`;
  } catch {
    return '';
  }
};

/* ----------------------- Component ----------------------- */

export const ListBlockRenderer: React.FC<ListBlockProps> = ({
  block,
  isDarkMode,
  citationSources = [],
}) => {
  const ListTag = block.ordered ? 'ol' : 'ul';

  return (
    <ListTag
      className={`my-5 ${
        block.ordered ? 'list-decimal' : 'list-disc'
      } list-outside pl-6 space-y-2 text-base leading-relaxed ${
        isDarkMode
          ? 'text-gray-200 marker:text-gray-400'
          : 'text-gray-900 marker:text-gray-500'
      }`}
      start={block.start}
    >
      {block.items.map((item, idx) => (
        <li key={idx} className="pl-1">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkMath]}
            rehypePlugins={[
              [
                rehypeKatex,
                { strict: false, throwOnError: false, trust: true },
              ],
            ]}
            components={{
              /* --- FORCE INLINE PARAGRAPHS --- */
              p: ({ children }) => (
                <span className="align-baseline">{children}</span>
              ),

              strong: ({ children }) => (
                <strong className="font-semibold">{children}</strong>
              ),

              em: ({ children }) => <em className="italic">{children}</em>,

              /* --- CRITICAL FIX: CUSTOM LINK RENDERER --- */
              a: ({ href }) => {
                if (!href) return null;

                const favicon = getHighQualityFavicon(href);
                const hostname = getHostname(href);

                return (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={hostname}
                    className={`inline-flex items-center justify-center ml-1 p-1 rounded-full transition-all ${
                      isDarkMode
                        ? 'hover:bg-white/10'
                        : 'hover:bg-black/5'
                    }`}
                  >
                    <div className="relative w-4 h-4 flex items-center justify-center">
                      {favicon && (
                        <img
                          src={favicon}
                          alt=""
                          className="w-4 h-4 rounded-full object-contain"
                          loading="lazy"
                          onError={(e) => {
                            const img = e.currentTarget;
                            img.style.display = 'none';
                            img.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      )}

                      <div className="hidden absolute inset-0 flex items-center justify-center">
                        <LinkIcon
                          className={`w-3 h-3 ${
                            isDarkMode
                              ? 'text-blue-400'
                              : 'text-blue-600'
                          }`}
                        />
                      </div>
                    </div>

                    <span className="sr-only">{href}</span>
                  </a>
                );
              },
            }}
          >
            {item.content}
          </ReactMarkdown>

          {/* OPTIONAL: Structured citation indexes (kept inline-safe) */}
          {item.reference_indexes &&
            item.reference_indexes.length > 0 && (
              <span className="inline-flex flex-wrap gap-1.5 ml-2 align-middle">
                {item.reference_indexes.map((refIndex, refIdx) => {
                  const source = citationSources[refIndex];
                  if (!source) return null;

                  const favicon = getHighQualityFavicon(source.link);
                  const hostname = getHostname(source.link);

                  return (
                    <a
                      key={refIdx}
                      href={source.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={source.title || hostname}
                      className={`inline-flex items-center justify-center p-0.5 rounded-full transition-all ${
                        isDarkMode
                          ? 'hover:bg-white/10'
                          : 'hover:bg-black/5'
                      }`}
                    >
                      <img
                        src={favicon}
                        alt=""
                        className="w-4 h-4 rounded-full object-contain"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </a>
                  );
                })}
              </span>
            )}
        </li>
      ))}
    </ListTag>
  );
};
